(() => {
  const ROOT_SELECTOR = '#main-collection-filters[data-deferred-drawer-filters="true"]';
  const DRAWER_TRIGGER_SELECTOR = '[data-deferred-facets-trigger]';
  const INLINE_DRAWER_TRIGGER_SELECTOR = '[data-deferred-facets-inline-trigger]';
  const FLOATING_DRAWER_TRIGGER_SELECTOR = '[data-deferred-facets-floating-trigger]';
  const DRAWER_CONTAINER_SELECTOR = '[data-deferred-facets-drawer-container]';
  const DRAWER_SOURCE_SELECTOR = '[data-deferred-facets-drawer-source]';
  const SORT_FORM_SELECTOR = '[data-deferred-sort-form]';
  const VIEW_MODE_SELECTOR = '.view-mode__button, .view-mode__button-mobile';
  const AVAILABILITY_TOGGLE_SELECTOR = '[data-availability-toggle="toggle"]';
  const AVAILABILITY_PARAM = 'filter.v.m.custom.availability';
  const LUIGE_VALUE = 'luige';
  const VIEW_MODE_STORAGE_KEYS = {
    desktop: 'mymaraCollectionGridColumnsDesktop',
    mobile: 'mymaraCollectionGridColumnsMobile'
  };
  const VIEW_MODE_ALLOWED_VALUES = {
    desktop: ['3', '4', '5'],
    mobile: ['1', '2']
  };
  let assetsPromise;
  let drawerPromise;
  let floatingTriggerFrame;
  let floatingTriggerBound = false;
  let drawerPrewarmBound = false;
  let idlePrewarmScheduled = false;
  let controlsObserver;

  const getRoot = () => document.querySelector(ROOT_SELECTOR);

  const getDrawerContainer = () => getRoot()?.querySelector(DRAWER_CONTAINER_SELECTOR) || null;

  const getDrawer = () => getDrawerContainer()?.querySelector('#drawer-filter') || null;

  const getInlineDrawerTrigger = () => getRoot()?.querySelector(INLINE_DRAWER_TRIGGER_SELECTOR) || null;

  const getFloatingDrawerTrigger = () => getRoot()?.querySelector(FLOATING_DRAWER_TRIGGER_SELECTOR) || null;

  const getHeaderBottom = () => {
    const header = document.querySelector('.section-header.shopify-section-group-header-group') || document.querySelector('.section-header');
    if (header) return Math.max(0, header.getBoundingClientRect().bottom);

    const styles = getComputedStyle(document.documentElement);
    const headerHeight = parseFloat(styles.getPropertyValue('--header-height'));
    const initialHeaderHeight = parseFloat(styles.getPropertyValue('--header-height-initial'));

    return headerHeight || initialHeaderHeight || 0;
  };

  const setFloatingTriggerVisibility = (isVisible) => {
    const floatingTrigger = getFloatingDrawerTrigger();
    if (!floatingTrigger) return;

    floatingTrigger.classList.toggle('is-visible', isVisible);
    floatingTrigger.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
    floatingTrigger.tabIndex = isVisible ? 0 : -1;
  };

  const updateFloatingTriggerVisibility = () => {
    const inlineTrigger = getInlineDrawerTrigger();
    const floatingTrigger = getFloatingDrawerTrigger();

    if (!inlineTrigger || !floatingTrigger) {
      setFloatingTriggerVisibility(false);
      return;
    }

    const inlineTriggerBounds = inlineTrigger.getBoundingClientRect();
    setFloatingTriggerVisibility(inlineTriggerBounds.bottom <= getHeaderBottom());
  };

  const requestFloatingTriggerVisibilityUpdate = () => {
    if (floatingTriggerFrame) return;

    floatingTriggerFrame = requestAnimationFrame(() => {
      floatingTriggerFrame = null;
      updateFloatingTriggerVisibility();
    });
  };

  const bindFloatingTriggerVisibility = () => {
    updateFloatingTriggerVisibility();

    if (floatingTriggerBound) return;
    floatingTriggerBound = true;

    window.addEventListener('scroll', requestFloatingTriggerVisibilityUpdate, { passive: true });
    window.addEventListener('resize', requestFloatingTriggerVisibilityUpdate, { passive: true });
  };

  const observeDeferredControls = () => {
    const root = getRoot();
    if (!root || controlsObserver || typeof MutationObserver === 'undefined') return;

    controlsObserver = new MutationObserver(() => {
      bindViewModeButtons();
      requestFloatingTriggerVisibilityUpdate();
    });
    controlsObserver.observe(root, { childList: true, subtree: true });
  };

  const COLLECTION_STATE_PARAM_PREFIXES = ['filter.', 'sort_by', 'page', 'q'];

  const removeCollectionStateParams = (urlSearchParams) => {
    Array.from(urlSearchParams.keys()).forEach((key) => {
      if (COLLECTION_STATE_PARAM_PREFIXES.some((prefix) => key === prefix || key.startsWith(prefix))) {
        urlSearchParams.delete(key);
      }
    });
  };

  const buildCollectionUrl = (searchParams = window.location.search.slice(1), { includeSectionId = false } = {}) => {
    const root = getRoot();
    if (!root) return null;

    const url = new URL(window.location.href);
    const nextSearchParams = new URLSearchParams(searchParams);

    removeCollectionStateParams(url.searchParams);

    const clearedKeys = new Set();
    nextSearchParams.forEach((value, key) => {
      if (!clearedKeys.has(key)) {
        url.searchParams.delete(key);
        clearedKeys.add(key);
      }
      url.searchParams.append(key, value);
    });

    if (includeSectionId) {
      url.searchParams.set('section_id', root.dataset.sectionId);
    } else {
      url.searchParams.delete('section_id');
    }

    url.searchParams.delete('drawer_section');
    url.searchParams.delete('pagination_section');
    return `${url.pathname}${url.search}`;
  };

  const buildSectionUrl = (searchParams = window.location.search.slice(1)) => {
    return buildCollectionUrl(searchParams, { includeSectionId: true });
  };

  const getConnection = () => navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;

  const canIdlePrewarmDrawer = () => {
    const connection = getConnection();
    if (!connection) return true;

    return connection.saveData !== true && !['slow-2g', '2g'].includes(connection.effectiveType);
  };

  const runWhenIdle = (callback) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 2500 });
      return;
    }

    window.setTimeout(callback, 700);
  };

  // The drawer is the same on every page, so its state ignores the page that infinite scroll writes into the URL
  const getDrawerStateSearch = (search = window.location.search) => {
    const searchParams = new URLSearchParams(search);
    searchParams.delete('page');
    return searchParams.toString();
  };

  const fetchSectionHtml = (url) =>
    fetch(url.toString()).then((response) => {
      if (!response.ok) throw new Error(`Failed to load filters: ${response.status}`);
      return response.text();
    });

  const fetchDrawerHtml = async (searchParams = window.location.search.slice(1)) => {
    const sectionUrl = buildSectionUrl(getDrawerStateSearch(searchParams));
    if (!sectionUrl) return null;

    const url = new URL(sectionUrl, window.location.origin);
    url.searchParams.set('drawer_section', '1');
    const html = await fetchSectionHtml(url);
    if (html.includes('data-deferred-facets-drawer-source')) return html;

    // The section picks its variant from the request URL parsed out of content_for_header, which Shopify
    // sometimes reports for a different request. The regular section render includes the drawer too.
    url.searchParams.delete('drawer_section');
    const fallbackHtml = await fetchSectionHtml(url);
    if (fallbackHtml.includes('data-deferred-facets-drawer-source')) return fallbackHtml;
    throw new Error('Filters response has no drawer');
  };

  const loadStylesheet = (id, href) => {
    if (!href) return Promise.resolve();
    if (document.getElementById(id)) return Promise.resolve();

    return new Promise((resolve) => {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = href;
      link.addEventListener('load', () => resolve(), { once: true });
      link.addEventListener('error', () => resolve(), { once: true });
      document.head.appendChild(link);
    });
  };

  const loadScript = (id, src) =>
    new Promise((resolve, reject) => {
      if (!src) {
        resolve();
        return;
      }

      const existing = document.getElementById(id);

      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
          return;
        }

        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = id;
      script.src = src;
      script.defer = true;
      script.addEventListener(
        'load',
        () => {
          script.dataset.loaded = 'true';
          resolve();
        },
        { once: true }
      );
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

  const ensureAssetsLoaded = () => {
    const root = getRoot();
    if (!root) return Promise.resolve();
    if (assetsPromise) return assetsPromise;

    const styleLoads = [
      loadStylesheet('component-facets-stylesheet', root.dataset.componentFacetsUrl),
      loadStylesheet('component-show-more-stylesheet', root.dataset.componentShowMoreUrl),
      loadStylesheet('component-swatch-input-stylesheet', root.dataset.componentSwatchInputUrl),
      loadStylesheet('component-swatch-stylesheet', root.dataset.componentSwatchUrl)
    ];

    if (root.dataset.hasPriceFilter === 'true') {
      styleLoads.push(loadStylesheet('nouislider-stylesheet', root.dataset.nouisliderCssUrl));
    }

    assetsPromise = Promise.all(styleLoads)
      .then(() => {
        const scriptLoads = [loadScript('show-more-script', root.dataset.showMoreScriptUrl)];

        if (root.dataset.hasPriceFilter === 'true') {
          scriptLoads.push(loadScript('nouislider-script', root.dataset.nouisliderScriptUrl));
        }

        return Promise.all(scriptLoads);
      })
      .then(() => loadScript('facets-script', root.dataset.facetsScriptUrl))
      .catch((error) => {
        assetsPromise = null;
        throw error;
      });

    return assetsPromise;
  };

  const applyViewMode = (button) => {
    const viewMode = button.getAttribute('data-view-mode');
    const viewModeType = button.classList.contains('view-mode__button-mobile') ? 'mobile' : 'desktop';

    applyViewModeValue(viewModeType, viewMode, true);
  };

  const isAllowedViewMode = (viewModeType, viewMode) => VIEW_MODE_ALLOWED_VALUES[viewModeType]?.includes(viewMode);

  const getStoredViewMode = (viewModeType) => {
    try {
      const storedViewMode = window.localStorage.getItem(VIEW_MODE_STORAGE_KEYS[viewModeType]);
      return isAllowedViewMode(viewModeType, storedViewMode) ? storedViewMode : null;
    } catch (error) {
      return null;
    }
  };

  const setStoredViewMode = (viewModeType, viewMode) => {
    if (!isAllowedViewMode(viewModeType, viewMode)) return;

    try {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEYS[viewModeType], viewMode);
    } catch (error) {
      // Ignore unavailable storage so the grid control still works normally.
    }
  };

  const setActiveViewModeButton = (viewModeType, viewMode) => {
    const selector = viewModeType === 'mobile' ? '.view-mode__button-mobile' : '.view-mode__button';

    document.querySelectorAll(selector).forEach((item) => {
      item.classList.toggle('active', item.getAttribute('data-view-mode') === viewMode);
    });
  };

  const applyViewModeValue = (viewModeType, viewMode, shouldStore = false) => {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid || !isAllowedViewMode(viewModeType, viewMode)) return;

    if (viewModeType === 'mobile') {
      productGrid.classList.remove('grid-cols-1', 'grid-cols-2');
      productGrid.classList.add(`grid-cols-${viewMode}`);
    } else {
      productGrid.classList.remove('lg:grid-cols-3', 'lg:grid-cols-4', 'lg:grid-cols-5');
      productGrid.classList.add(`lg:grid-cols-${viewMode}`);
    }

    setActiveViewModeButton(viewModeType, viewMode);

    if (shouldStore) {
      setStoredViewMode(viewModeType, viewMode);
    }
  };

  const applyStoredViewModes = () => {
    if (document.querySelector('.view-mode__button-mobile')) {
      applyViewModeValue('mobile', getStoredViewMode('mobile'));
    }

    if (document.querySelector('.view-mode__button')) {
      applyViewModeValue('desktop', getStoredViewMode('desktop'));
    }
  };

  const bindViewModeButtons = () => {
    document.querySelectorAll(VIEW_MODE_SELECTOR).forEach((button) => {
      if (button.dataset.viewModeBound === 'true') return;
      button.dataset.viewModeBound = 'true';
      button.addEventListener('click', () => applyViewMode(button));
    });

    applyStoredViewModes();
  };

  const submitSortForm = (form) => {
    if (!form) return;

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
      return;
    }

    form.submit();
  };

  const markDrawerMounted = (container) => {
    if (container) {
      container.dataset.drawerMounted = 'true';
    }

    const root = getRoot();
    if (root) {
      root.dataset.drawerMounted = 'true';
    }
  };

  const syncAttributes = (target, source, preservedAttributes = []) => {
    const preserved = new Set(preservedAttributes);

    target.getAttributeNames().forEach((name) => {
      if (preserved.has(name)) return;
      if (!source.hasAttribute(name)) {
        target.removeAttribute(name);
      }
    });

    source.getAttributeNames().forEach((name) => {
      if (preserved.has(name)) return;
      target.setAttribute(name, source.getAttribute(name) ?? '');
    });
  };

  const replaceDrawerFromHtml = (htmlOrDocument, options = {}) => {
    const container = getDrawerContainer();
    if (!container) return null;

    const parsedHtml = typeof htmlOrDocument === 'string'
      ? new DOMParser().parseFromString(htmlOrDocument, 'text/html')
      : htmlOrDocument;
    const source = parsedHtml.querySelector(DRAWER_SOURCE_SELECTOR);
    if (!source) return null;

    const sourceDrawer = source.querySelector('#drawer-filter');
    if (!sourceDrawer) return null;

    const currentDrawer = getDrawer();
    const trigger = options.trigger || document.querySelector(DRAWER_TRIGGER_SELECTOR);

    if (currentDrawer) {
      const wasOpen = currentDrawer.hasAttribute('open');
      const drawerContentFilter = currentDrawer.querySelector('.drawer-content-filter');
      const drawerScrollTop = drawerContentFilter ? drawerContentFilter.scrollTop : 0;

      syncAttributes(currentDrawer, sourceDrawer, wasOpen ? ['open'] : []);
      currentDrawer.innerHTML = sourceDrawer.innerHTML;
      currentDrawer.refreshElements?.();
      markDrawerMounted(container);

      if (wasOpen) {
        currentDrawer.triggerButton = trigger || currentDrawer.triggerButton || document.querySelector(DRAWER_TRIGGER_SELECTOR);
        currentDrawer.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
        currentDrawer.drawer?.setAttribute('aria-hidden', 'false');
        document.addEventListener('focus', currentDrawer.trapFocus, true);

        const nextDrawerContentFilter = currentDrawer.querySelector('.drawer-content-filter');
        if (nextDrawerContentFilter) {
          nextDrawerContentFilter.scrollTop = drawerScrollTop;
        }

        return currentDrawer;
      }

      if (options.open === true && typeof currentDrawer.open === 'function') {
        requestAnimationFrame(() => {
          currentDrawer.open(trigger);
        });
      } else {
        currentDrawer.drawer?.setAttribute('aria-hidden', 'true');
      }

      return currentDrawer;
    }

    container.innerHTML = source.innerHTML;
    markDrawerMounted(container);

    const nextDrawer = getDrawer();
    if (options.open === true && nextDrawer && typeof nextDrawer.open === 'function') {
      requestAnimationFrame(() => {
        nextDrawer.open(trigger);
      });
    }

    return nextDrawer;
  };

  const ensureDrawerLoaded = ({ open = false, trigger = null, searchParams = window.location.search.slice(1) } = {}) => {
    const existingDrawer = getDrawer();
    if (existingDrawer) {
      if (open && typeof existingDrawer.open === 'function' && !existingDrawer.hasAttribute('open')) {
        existingDrawer.open(trigger || document.querySelector(DRAWER_TRIGGER_SELECTOR));
      }
      return Promise.resolve(existingDrawer);
    }

    if (drawerPromise) {
      return drawerPromise.then((drawer) => {
        if (open && drawer && typeof drawer.open === 'function') {
          drawer.open(trigger || document.querySelector(DRAWER_TRIGGER_SELECTOR));
        }
        return drawer;
      });
    }

    const requestRoot = getRoot();
    const requestSearch = getDrawerStateSearch();
    const pending = Promise.all([ensureAssetsLoaded(), fetchDrawerHtml(searchParams)])
      .then(([, html]) => {
        if (!html) return null;
        if (requestRoot !== getRoot() || requestSearch !== getDrawerStateSearch()) {
          drawerPromise = null;
          return ensureDrawerLoaded({ open, trigger, searchParams: window.location.search.slice(1) });
        }
        const mounted = getDrawer();
        if (mounted) {
          if (open) mounted.open?.(trigger);
          return mounted;
        }
        return replaceDrawerFromHtml(html, { open, trigger });
      })
      .finally(() => {
        if (drawerPromise === pending) drawerPromise = null;
      });

    drawerPromise = pending;
    return pending;
  };

  const prewarmDrawer = () => ensureDrawerLoaded().catch(() => {});

  const bindDrawerPrewarm = () => {
    if (drawerPrewarmBound) return;
    drawerPrewarmBound = true;

    const prewarmOnIntent = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || !target.closest(DRAWER_TRIGGER_SELECTOR)) return;
      prewarmDrawer();
    };

    document.addEventListener('pointerover', prewarmOnIntent, { passive: true });
    document.addEventListener('pointerdown', prewarmOnIntent, { passive: true });
    document.addEventListener('touchstart', prewarmOnIntent, { passive: true });
    document.addEventListener('focusin', prewarmOnIntent);
  };

  const scheduleIdleDrawerPrewarm = () => {
    if (idlePrewarmScheduled || isDrawerMounted() || !canIdlePrewarmDrawer()) return;
    idlePrewarmScheduled = true;

    const scheduleAfterLoad = () => {
      window.setTimeout(() => {
        runWhenIdle(() => {
          if (!isEnabled() || isDrawerMounted()) return;
          prewarmDrawer();
        });
      }, 1200);
    };

    if (document.readyState === 'complete') {
      scheduleAfterLoad();
      return;
    }

    window.addEventListener('load', scheduleAfterLoad, { once: true });
  };

  const isEnabled = () => Boolean(getRoot());

  const isDrawerMounted = () => {
    const root = getRoot();
    if (!root) return false;
    return root.dataset.drawerMounted === 'true' && Boolean(getDrawer());
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(DRAWER_TRIGGER_SELECTOR);
    if (!trigger || !isEnabled()) return;

    event.preventDefault();

    ensureDrawerLoaded({ open: true, trigger }).catch(() => {
      window.location.assign(window.location.href);
    });
  });

  document.addEventListener('change', (event) => {
    const sortSelect = event.target.closest(`${SORT_FORM_SELECTOR} select`);
    if (!sortSelect || !isEnabled()) return;

    submitSortForm(sortSelect.form);
  });

  document.addEventListener(
    'click',
    (event) => {
      const toggleButton = event.target.closest(AVAILABILITY_TOGGLE_SELECTOR);
      if (!toggleButton || !isEnabled()) return;

      event.preventDefault();

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete('page');
      const isLuigeActive = searchParams.get(AVAILABILITY_PARAM) === LUIGE_VALUE;

      if (isLuigeActive) {
        searchParams.delete(AVAILABILITY_PARAM);
      } else {
        searchParams.set(AVAILABILITY_PARAM, LUIGE_VALUE);
      }

      const nextSearchParams = searchParams.toString();

      if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
        ensureDrawerLoaded({ searchParams: nextSearchParams })
          .then(() => FacetFiltersForm.renderPage(nextSearchParams, null))
          .catch(() => {
            const nextUrl = buildCollectionUrl(nextSearchParams);
            if (!nextUrl) return;
            window.location.assign(nextUrl);
          });
        return;
      }

      const nextUrl = buildCollectionUrl(nextSearchParams);
      if (!nextUrl) return;
      window.location.assign(nextUrl);
    },
    { passive: false }
  );

  const initializeDeferredDrawer = () => {
    if (!isEnabled()) return;

    bindViewModeButtons();
    bindFloatingTriggerVisibility();
    bindDrawerPrewarm();
    observeDeferredControls();

    const controls = document.getElementById('DrawerFacetsControls');
    if (!controls) return;

    if (controls.dataset.hasActiveFilters === 'true') {
      ensureDrawerLoaded().catch(() => {});
      return;
    }

    scheduleIdleDrawerPrewarm();
  };

  window.MyMaraDeferredFacets = {
    ensureAssetsLoaded,
    ensureDrawerLoaded,
    isDrawerMounted,
    isEnabled,
    replaceDrawerFromHtml
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeDeferredDrawer, { once: true });
  } else {
    initializeDeferredDrawer();
  }
})();
